import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../app/AuthContext";

type SocialStatus = "queued" | "approved" | "posted" | "skipped" | "failed" | "all";
type SocialPost = Awaited<ReturnType<typeof api.admin.socialPosts>>["items"][number];
type SocialSummary = Awaited<ReturnType<typeof api.admin.socialSummary>>;
type SocialPublishStatus = Awaited<ReturnType<typeof api.admin.socialPublishStatus>>;

const directInstagramPublishPaused = true;
const storedPosterCache = new Map<string, string>();
const storedPosterPromises = new Map<string, Promise<{ url: string; blob: Blob }>>();
const posterUploadQueue: Array<() => void> = [];
let activePosterUploads = 0;
const maxActivePosterUploads = 2;

export function AdminSocialPage() {
  const { user } = useAuth();
  const [status, setStatus] = useState<SocialStatus>("queued");
  const [items, setItems] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [summary, setSummary] = useState<SocialSummary | null>(null);
  const [publishStatus, setPublishStatus] = useState<SocialPublishStatus | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.admin.socialPosts(status), api.admin.socialSummary(), api.admin.socialPublishStatus()])
      .then(([posts, nextSummary, nextPublishStatus]) => {
        setItems(posts.items);
        setSummary(nextSummary);
        setPublishStatus(nextPublishStatus);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    setSelected([]);
    load();
  }, [status]);

  if (!user || (user.role !== "admin" && user.role !== "super_admin")) {
    return <div className="container empty-state">Admin access required.</div>;
  }

  const generate = async () => {
    setBusy(true);
    setMessage(null);
    try {
      const result = await api.admin.socialGenerate(12);
      const repaired = result.imageRepair?.repaired ?? 0;
      setMessage(`Generated ${result.created} post candidates${repaired ? ` and repaired ${repaired} image(s)` : ""}.`);
      load();
    } finally {
      setBusy(false);
    }
  };

  const approveSafe = async () => {
    setBusy(true);
    setMessage("Approving safe top picks...");
    try {
      const result = await api.admin.socialApproveSafe(3);
      const skipped = [
        result.skippedWeakImages ? `${result.skippedWeakImages} weak image(s)` : "",
        result.skippedMissingPoster ? `${result.skippedMissingPoster} missing poster(s)` : "",
      ].filter(Boolean);
      setMessage(`Approved ${result.approved} poster-ready post(s)${skipped.length ? `, skipped ${skipped.join(" and ")}` : ""}.`);
      load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Safe approval failed.");
    } finally {
      setBusy(false);
    }
  };

  const cleanReadiness = async () => {
    setBusy(true);
    setMessage("Cleaning queue readiness...");
    try {
      const result = await api.admin.socialCleanReadiness();
      setMessage(result.movedToQueued
        ? `Moved ${result.movedToQueued} approved post(s) without stored posters back to queued.`
        : "Queue readiness is clean.");
      load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Queue cleanup failed.");
    } finally {
      setBusy(false);
    }
  };

  const setPostStatus = async (id: string, nextStatus: Exclude<SocialStatus, "all">) => {
    await api.admin.socialSetStatus(id, nextStatus);
    setMessage(nextStatus === "posted" ? "Marked as posted." : `Moved to ${nextStatus}.`);
    load();
  };

  const bulkStatus = async (nextStatus: Exclude<SocialStatus, "all">) => {
    if (selected.length === 0) return;
    if ((nextStatus === "skipped" || nextStatus === "posted") && !window.confirm(`Move ${selected.length} social post(s) to ${nextStatus}?`)) return;
    setBusy(true);
    try {
      const result = await api.admin.socialBulkStatus(selected, nextStatus);
      setMessage(`Moved ${result.updated} post(s) to ${nextStatus}.`);
      setSelected([]);
      load();
    } finally {
      setBusy(false);
    }
  };

  const publishPost = async (post: SocialPost) => {
    setMessage(`Publishing ${post.brand_name} to Instagram...`);
    try {
      const result = await api.admin.socialPublish(post.id);
      setMessage(result.ok ? `Published ${post.brand_name} to Instagram.` : result.message || "Instagram publish failed.");
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Instagram publish failed.");
    } finally {
      load();
    }
  };

  const repairPostImage = async (post: SocialPost) => {
    setMessage(`Repairing image for ${post.brand_name}...`);
    try {
      const result = await api.admin.socialRepairImage(post.id);
      setMessage(result.message);
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "No stronger image found.");
    } finally {
      load();
    }
  };

  const repairQueueImages = async () => {
    setBusy(true);
    setMessage("Repairing weak queued images...");
    try {
      const result = await api.admin.socialRepairImages(36);
      setMessage(`Repaired ${result.repaired} social image(s), skipped ${result.skipped}.`);
      load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Bulk image repair failed.");
    } finally {
      setBusy(false);
    }
  };

  const testInstagram = async () => {
    setBusy(true);
    setMessage("Testing Instagram connection...");
    try {
      const result = await api.admin.socialTestInstagram();
      setMessage(result.message);
      load();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : "Instagram connection test failed.");
    } finally {
      setBusy(false);
    }
  };

  const notify = (text: string) => {
    setMessage(text);
    window.setTimeout(() => setMessage((current) => current === text ? null : current), 2800);
  };

  const toggleSelected = (id: string) => {
    setSelected((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };
  const visibleIds = items.map((item) => item.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleAllVisible = () => setSelected(allVisibleSelected ? [] : visibleIds);

  return (
    <div className="container admin-social-page" style={{ paddingBlock: "2rem" }}>
      <div className="admin-page-head">
        <div>
          <h2>Social queue</h2>
          <p style={{ color: "var(--fg-muted)", marginTop: "0.75rem", maxWidth: "68ch" }}>
            Automated Instagram-ready posts for brands with strong discovery signal, youth relevance, and usable images.
          </p>
        </div>
        <div className="admin-page-actions">
          <button disabled={busy} onClick={cleanReadiness}>
            {busy ? "Working..." : "Clean readiness"}
          </button>
          <button disabled={busy} onClick={repairQueueImages}>
            {busy ? "Working..." : "Repair queue images"}
          </button>
          <button disabled={busy || (summary?.counts.queued ?? 0) === 0} onClick={approveSafe}>
            {busy ? "Working..." : "Approve top 3 safe"}
          </button>
          <button className="primary" disabled={busy} onClick={generate}>
            {busy ? "Working..." : "Generate candidates"}
          </button>
        </div>
      </div>

      <div className="filter-bar social-status-tabs" style={{ marginTop: "1.5rem" }}>
        {(["queued", "approved", "failed", "posted", "skipped", "all"] as const).map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={status === s ? "primary" : ""}>
            {s} <span className="filter-count">{summary ? (s === "all" ? summary.total : summary.counts[s]) : ""}
            </span>
          </button>
        ))}
      </div>

      {summary && (
        <section className="social-summary" aria-label="Social queue summary">
          <div>
            <span>Total posts</span>
            <b>{summary.total}</b>
          </div>
          <div>
            <span>Queued</span>
            <b>{summary.counts.queued}</b>
          </div>
          <div>
            <span>Ready</span>
            <b>{summary.readyToPost}</b>
          </div>
          <div className={summary.needsPoster > 0 ? "is-warning" : ""}>
            <span>Needs poster</span>
            <b>{summary.needsPoster}</b>
          </div>
          <div>
            <span>Posted</span>
            <b>{summary.counts.posted}</b>
          </div>
          <div className={summary.weakImages > 0 ? "is-warning" : ""}>
            <span>Weak images</span>
            <b>{summary.weakImages}</b>
          </div>
        </section>
      )}

      {publishStatus && summary && (
        <section className={`social-publish-health ${directInstagramPublishPaused ? "is-ready" : publishStatus.ready ? "is-ready" : "is-blocked"}`}>
          <div>
            <span>Instagram workflow</span>
            <b>{directInstagramPublishPaused ? "Manual mode" : publishStatus.ready ? "Ready" : "Setup needed"}</b>
            <p>
              {directInstagramPublishPaused
                ? `Direct API publishing is paused for launch. Use Save / Share poster, copy the caption, post manually to @beforemainstreamcom, then mark the post as posted here. ${summary.readyToPost} approved post(s) are ready.`
                : publishStatus.ready
                  ? `Cron approves ${publishStatus.autoApproveSafeLimit} safe queued post(s), then publishes ${publishStatus.autoPublishLimit} ${publishStatus.autoPublishMode} post(s) per run. ${summary.readyToPost} approved post(s) are ready.`
                  : `Missing ${publishStatus.missing.join(" and ")}. Manual save/share still works.`}
            </p>
            {!directInstagramPublishPaused && (
              <button disabled={busy || !publishStatus.ready} onClick={testInstagram}>
                {busy ? "Testing..." : "Test Instagram connection"}
              </button>
            )}
          </div>
          <dl>
            <div>
              <dt>Posting</dt>
              <dd>{directInstagramPublishPaused ? "Manual" : "API"}</dd>
            </div>
            <div>
              <dt>Poster</dt>
              <dd>Stored PNG</dd>
            </div>
            <div>
              <dt>IG profile</dt>
              <dd>@beforemainstreamcom</dd>
            </div>
            {!directInstagramPublishPaused && (
              <>
                <div>
                  <dt>Graph</dt>
                  <dd>{publishStatus.graphVersion}</dd>
                </div>
                <div>
                  <dt>Auto approve</dt>
                  <dd>{publishStatus.autoApproveSafeLimit}</dd>
                </div>
              </>
            )}
          </dl>
        </section>
      )}

      {items.length > 0 && (
        <div className="social-bulkbar">
          <button onClick={toggleAllVisible}>{allVisibleSelected ? "Clear selection" : "Select visible"}</button>
          <span>{selected.length} selected</span>
          <button disabled={selected.length === 0 || busy} onClick={() => bulkStatus("approved")}>Approve selected</button>
          <button disabled={selected.length === 0 || busy} onClick={() => bulkStatus("skipped")}>Skip selected</button>
          <button disabled={selected.length === 0 || busy} onClick={() => bulkStatus("queued")}>Back to queue</button>
        </div>
      )}

      {message && (
        <>
          <p style={{ color: "var(--accent)", marginTop: "1rem" }}>{message}</p>
          <div className="admin-feedback-toast" role="status">{message}</div>
        </>
      )}

      {loading ? <div className="empty-state">Loading...</div> : items.length === 0 ? (
        <div className="empty-state">No posts in this bucket yet.</div>
      ) : (
        <div className="social-post-list">
          {items.map((post) => (
            <article key={post.id} className="social-post-card">
              <div className="social-post-card__poster">
                <label className="social-post-card__select">
                  <input
                    type="checkbox"
                    checked={selected.includes(post.id)}
                    onChange={() => toggleSelected(post.id)}
                    aria-label={`Select ${post.brand_name}`}
                  />
                </label>
                <div className={`social-post-card__status social-post-card__status--${post.status}`}>
                  <span>{post.status}</span>
                  <b>{post.score}</b>
                </div>
                <SocialPosterPreview post={post} />
                <div className="social-post-card__mobile-actions">
                  <div className="social-post-card__actions-label">
                    <span>Post flow</span>
                    <b>{post.brand_name}</b>
                  </div>
                  <SocialPostActions post={post} setPostStatus={setPostStatus} publishPost={publishPost} repairPostImage={repairPostImage} notify={notify} />
                </div>
              </div>
              <div className="social-post-card__content">
                <div className="social-post-card__head">
                  <div className="social-post-card__title">
                    <h3 style={{ margin: 0 }}>
                      <Link to={`/admin/brands/${post.brand_id}`}>{post.brand_name}</Link>{" "}
                      <a href={`/brands/${post.brand_slug}`} target="_blank" rel="noreferrer" style={{ color: "var(--fg-subtle)" }}>open</a>
                    </h3>
                    <p style={{ color: "var(--fg-subtle)", marginTop: "0.35rem" }}>
                      {post.status} / score {post.score}{post.instagram_handle ? ` / @${post.instagram_handle}` : ""}
                    </p>
                    {post.weak_image && <p className="social-post-card__warning">Weak image candidate. Repair before posting.</p>}
                    {!post.image_url && <p className="social-post-card__warning is-soft">Poster fallback active. Use Save / Share poster or Download PNG.</p>}
                  </div>
                  <div className="social-post-card__desktop-actions">
                    <SocialPostActions post={post} setPostStatus={setPostStatus} publishPost={publishPost} repairPostImage={repairPostImage} notify={notify} />
                  </div>
                </div>
                <div className="social-post-card__details">
                  <div className="social-post-card__details-label">Caption and signals</div>
                  <textarea className="social-post-card__caption" rows={10} readOnly value={post.caption} />
                  {post.reason_json && <ReasonLine reasonJson={post.reason_json} />}
                  {post.publish_error && <p className="social-post-card__error">{post.publish_error}</p>}
                  {post.instagram_media_id && <p className="social-post-card__published">Instagram media {post.instagram_media_id}</p>}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function SocialPostActions({
  post,
  setPostStatus,
  publishPost,
  repairPostImage,
  notify,
}: {
  post: SocialPost;
  setPostStatus: (id: string, nextStatus: Exclude<SocialStatus, "all">) => Promise<void>;
  publishPost: (post: SocialPost) => Promise<void>;
  repairPostImage: (post: SocialPost) => Promise<void>;
  notify: (text: string) => void;
}) {
  const copyCaption = async () => {
    await navigator.clipboard.writeText(post.caption);
    notify(`Caption copied for ${post.brand_name}.`);
  };

  const savePoster = async () => {
    await sharePoster(post);
    notify(`Poster ready for ${post.brand_name}.`);
  };

  const markPosted = async () => {
    if (!window.confirm(`Mark ${post.brand_name} as posted?`)) return;
    await setPostStatus(post.id, "posted");
  };

  return (
    <div className="social-post-card__actions">
      <div className="social-post-card__action-group social-post-card__action-group--primary">
        <span>Posting steps</span>
        <button className="primary" onClick={savePoster}>1. Save / Share poster</button>
        <button onClick={copyCaption}>2. Copy caption</button>
        <a className="buttonish" href="https://www.instagram.com/beforemainstreamcom/" target="_blank" rel="noreferrer">3. Open Instagram</a>
        <button className="primary" onClick={markPosted}>4. Mark posted</button>
      </div>
      <div className="social-post-card__action-group social-post-card__action-group--secondary">
        <span>Admin controls</span>
        {!directInstagramPublishPaused && (
          <button className="primary" disabled={!post.poster_url && (!post.image_url || post.weak_image)} onClick={() => publishPost(post)}>Publish via API</button>
        )}
        <button onClick={() => repairPostImage(post)}>Repair image</button>
        <button onClick={() => downloadPoster(post)}>Download PNG</button>
        <button onClick={() => setPostStatus(post.id, "approved")}>Move to approved</button>
        <button onClick={() => setPostStatus(post.id, "skipped")}>Skip post</button>
      </div>
    </div>
  );
}

function SocialPosterPreview({ post }: { post: SocialPost }) {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (post.poster_url) {
      setSrc(post.poster_url);
      return () => { cancelled = true; };
    }

    setSrc(null);
    ensureStoredPoster(post)
      .then(({ url }) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setSrc(null);
      });
    return () => { cancelled = true; };
  }, [post.id, post.image_url, post.caption, post.reason_json, post.score, post.poster_url]);

  return src ? (
    <img
      className="social-poster-image"
      src={src}
      alt={`Instagram poster preview for ${post.brand_name}`}
    />
  ) : (
    <div className="social-poster-loading">Rendering poster...</div>
  );
}

function ReasonLine({ reasonJson }: { reasonJson: string }) {
  try {
    const reason = JSON.parse(reasonJson) as Record<string, string | number | null>;
    return (
      <p style={{ color: "var(--fg-subtle)", margin: 0 }}>
        Discovery {reason.discovery} / mainstream {reason.mainstream} / momentum {reason.momentum}
        {reason.style ? ` / ${reason.style}` : ""}{reason.category ? ` / ${reason.category}` : ""}
      </p>
    );
  } catch {
    return null;
  }
}

function parseReason(reasonJson: string | null): Record<string, string | number | null> {
  try { return reasonJson ? JSON.parse(reasonJson) as Record<string, string | number | null> : {}; }
  catch { return {}; }
}

function extractSummary(caption: string): string {
  const lines = caption.split("\n").map((line) => line.trim()).filter(Boolean);
  return lines[1] && !lines[1].startsWith("Radar signal") ? lines[1] : "Find brands before they become mainstream.";
}

async function downloadPoster(post: SocialPost): Promise<void> {
  const { url, blob } = await ensureStoredPoster(post);
  const link = document.createElement("a");
  link.download = posterFilename(post);
  link.href = post.poster_url || url || URL.createObjectURL(blob);
  link.click();
  if (!post.poster_url && !url) URL.revokeObjectURL(link.href);
}

async function sharePoster(post: SocialPost): Promise<void> {
  const { blob } = await ensureStoredPoster(post);
  const file = new File([blob], posterFilename(post), { type: "image/png" });
  const shareData = {
    title: `${post.brand_name} on BeforeMainstream`,
    text: "Find brands before they become mainstream.",
    files: [file],
  };

  if (navigator.canShare?.(shareData)) {
    await navigator.share(shareData);
    return;
  }

  await downloadPoster(post);
}

async function ensureStoredPoster(post: SocialPost): Promise<{ url: string; blob: Blob }> {
  const existing = storedPosterPromises.get(post.id);
  if (existing) return existing;

  const promise = enqueuePosterUpload(() => ensureStoredPosterNow(post));
  storedPosterPromises.set(post.id, promise);
  promise.finally(() => storedPosterPromises.delete(post.id));
  return promise;
}

async function ensureStoredPosterNow(post: SocialPost): Promise<{ url: string; blob: Blob }> {
  const existingUrl = post.poster_url || storedPosterCache.get(post.id);
  if (existingUrl) {
    try {
      const blob = await fetch(existingUrl).then((res) => {
        if (!res.ok) throw new Error("Stored poster could not be loaded.");
        return res.blob();
      });
      return { url: existingUrl, blob };
    } catch {
      storedPosterCache.delete(post.id);
    }
  }

  const canvas = await renderPosterCanvas(post);
  const blob = await canvasToBlob(canvas);
  const stored = await api.admin.socialStorePoster(post.id, blob);
  storedPosterCache.set(post.id, stored.posterUrl);
  return { url: stored.posterUrl, blob };
}

function enqueuePosterUpload<T>(job: () => Promise<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    posterUploadQueue.push(() => {
      activePosterUploads += 1;
      job()
        .then(resolve, reject)
        .finally(() => {
          activePosterUploads -= 1;
          runNextPosterUpload();
        });
    });
    runNextPosterUpload();
  });
}

function runNextPosterUpload() {
  while (activePosterUploads < maxActivePosterUploads && posterUploadQueue.length > 0) {
    const next = posterUploadQueue.shift();
    next?.();
  }
}

async function renderPosterCanvas(post: SocialPost): Promise<HTMLCanvasElement> {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available.");

  const reason = parseReason(post.reason_json);
  const image = post.image_url ? await loadImage(post.image_url).catch(() => null) : null;
  const accent = "#c6ff3a";
  const bg = "#090c0c";
  const panel = "#101114";
  const muted = "#a5a8b2";

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  roundRect(ctx, 48, 44, 984, 1262, 46, panel);

  if (image) drawCoverImage(ctx, image, 48, 44, 984, 650);
  else {
    drawPosterFallback(ctx, post, 48, 44, 984, 650);
  }
  const fade = ctx.createLinearGradient(0, 520, 0, 760);
  fade.addColorStop(0, "rgba(16,17,20,0)");
  fade.addColorStop(1, panel);
  ctx.fillStyle = fade;
  ctx.fillRect(48, 500, 984, 230);

  pill(ctx, 82, 80, "▲ RADAR", accent, "#071006", 30);

  let y = 760;
  ctx.fillStyle = "#6d7280";
  ctx.font = "700 30px ui-monospace, SFMono-Regular, Menlo, monospace";
  ctx.letterSpacing = "3px";
  ctx.fillText(`${String(reason.category || "BRAND").toUpperCase()} · ${String(reason.style || "RADAR").toUpperCase()}`, 86, y);
  ctx.letterSpacing = "0px";

  y += 86;
  ctx.fillStyle = "#f4f4f5";
  ctx.font = "800 76px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  y = wrapText(ctx, post.brand_name, 86, y, 890, 80, 2);

  y += 28;
  ctx.fillStyle = muted;
  ctx.font = "500 38px ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
  y = wrapText(ctx, extractSummary(post.caption), 86, y, 880, 52, 2);

  y += 26;
  const tags = [reason.category, reason.style, `Signal ${post.score}`].filter(Boolean).map(String).slice(0, 3);
  let x = 86;
  for (const tag of tags) {
    const hot = tag === reason.style;
    x = pill(ctx, x, y, tag.toUpperCase(), hot ? accent : "#2a333b", hot ? "#071006" : "#f4f4f5", 24) + 14;
  }

  ctx.strokeStyle = "#20252b";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(86, 1210);
  ctx.lineTo(994, 1210);
  ctx.stroke();

  ctx.fillStyle = "#f4f4f5";
  ctx.font = "800 36px ui-sans-serif, system-ui, sans-serif";
  ctx.fillText("BeforeMainstream", 86, 1266);
  ctx.fillStyle = accent;
  ctx.font = "700 24px ui-monospace, SFMono-Regular, Menlo, monospace";
  drawRightAlignedText(ctx, "beforemainstream.com", 994, 1266, 390);

  return canvas;
}

function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Could not create poster image."));
    }, "image/png");
  });
}

function posterFilename(post: SocialPost): string {
  return `${post.brand_slug}-beforemainstream-poster.png`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function drawCoverImage(ctx: CanvasRenderingContext2D, img: HTMLImageElement, x: number, y: number, w: number, h: number) {
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight);
  const sw = w / scale;
  const sh = h / scale;
  const sx = (img.naturalWidth - sw) / 2;
  const sy = (img.naturalHeight - sh) / 2;
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 46);
  ctx.clip();
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
  ctx.restore();
}

function drawPosterFallback(ctx: CanvasRenderingContext2D, post: SocialPost, x: number, y: number, w: number, h: number) {
  const gradient = ctx.createLinearGradient(x, y, x + w, y + h);
  gradient.addColorStop(0, "#14191a");
  gradient.addColorStop(0.55, "#101114");
  gradient.addColorStop(1, "#1d2719");
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 46);
  ctx.clip();
  ctx.fillStyle = gradient;
  ctx.fillRect(x, y, w, h);

  ctx.fillStyle = "rgba(198,255,58,0.08)";
  ctx.font = "900 156px ui-sans-serif, system-ui, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(post.brand_name.toUpperCase(), x + w / 2, y + h / 2 - 8, w - 96);

  ctx.strokeStyle = "rgba(198,255,58,0.28)";
  ctx.lineWidth = 3;
  ctx.setLineDash([18, 18]);
  ctx.strokeRect(x + 34, y + 34, w - 68, h - 68);
  ctx.setLineDash([]);

  ctx.fillStyle = "#f4f4f5";
  ctx.font = "800 52px ui-sans-serif, system-ui, sans-serif";
  ctx.fillText("RADAR FIND", x + w / 2, y + h / 2 + 82);
  ctx.textAlign = "left";
  ctx.restore();
}

function pill(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, bg: string, fg: string, size: number): number {
  ctx.font = `800 ${size}px ui-monospace, SFMono-Regular, Menlo, monospace`;
  ctx.letterSpacing = "0px";
  const width = Math.ceil(ctx.measureText(text).width) + 42;
  roundRect(ctx, x, y, width, size + 28, (size + 28) / 2, bg);
  ctx.fillStyle = fg;
  ctx.fillText(text, x + 21, y + size + 8);
  return x + width;
}

function drawRightAlignedText(ctx: CanvasRenderingContext2D, text: string, rightX: number, y: number, maxWidth: number) {
  let current = text;
  while (ctx.measureText(current).width > maxWidth && current.length > 3) {
    current = current.slice(0, -1);
  }
  if (current !== text) current = `${current.slice(0, -3)}...`;
  ctx.fillText(current, rightX - ctx.measureText(current).width, y);
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, lineHeight: number, maxLines: number): number {
  const words = text.split(/\s+/);
  let line = "";
  let lines = 0;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines += 1;
      ctx.fillText(lines === maxLines ? `${line}...` : line, x, y);
      if (lines >= maxLines) return y;
      line = word;
      y += lineHeight;
    } else {
      line = test;
    }
  }
  if (line && lines < maxLines) ctx.fillText(line, x, y);
  return y + lineHeight;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number, fill: string) {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.fill();
}
